import { useEffect, useState } from 'react'
import { Alert, Button, Modal, Space, Typography, Upload } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { UploadOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatBytes } from '@/shared/lib/format'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import type { OperationResult } from '@/shared/api/operationResult'
import { AuthedImage } from './AuthedImage'
import { localizeOutcome } from './outcomeText'
import {
  EMPLOYEE_PHOTO_SOFT_MAX_BYTES,
  employeePhotoUrl,
  isJpeg,
  uploadEmployeePhoto,
  type AttendanceEmployee,
} from './attendanceApi'

interface EmployeePhotoModalProps {
  /** `null` = oyna yopiq */
  employee: AttendanceEmployee | null
  onClose: () => void
}

/**
 * Yuz rasmini yuklash.
 *
 * <h2>⚠️ RASM QURILMAGA KETADI, PANELGA EMAS</h2>
 * Bu yerdagi fayl xodim kartochkasidagi avatar emas — u Hikvision yuz
 * kutubxonasiga yoziladi va AYNAN shu rasm bo'yicha odam eshikdan
 * o'tadi. Shuning uchun oyna qurilmaning talablarini ochiq aytadi:
 * yaroqsiz rasm "avatar chiroyli chiqmadi" emas, "xodim eshikdan o'ta
 * olmaydi" degani.
 *
 * <h2>⚠️ JPEG — QAT'IY, HAJM — OGOHLANTIRISH</h2>
 * Ikkalasi ATAYLAB boshqacha ishlaydi:
 *   · format — bloklanadi. Qurilma PNG ni umuman qabul qilmaydi, ya'ni
 *     yuborishning ma'nosi yo'q va natija tushunarsiz `subStatusCode`
 *     bo'lardi
 *   · hajm — faqat ogohlantiriladi. Aniq chegara SERVERDA va qurilmada,
 *     panelda emas. O'zimizdan chegara qo'yib bloklash serverda ishlaydigan
 *     rasmni yuklashga to'sqinlik qilardi va operator sababini hech
 *     qayerdan bilmasdi
 *
 * <h2>⚠️ OLDINDAN KO'RISH SHART</h2>
 * Yuklangan rasm qaytarib olinmaydi (u eski rasmning ustiga yoziladi va
 * qurilmaga ketadi). Fayl nomiga qarab tanlash xato — `photo_2.jpg` ikki
 * xodimda ham bo'lishi mumkin. Rasmni KO'RIB tasdiqlash bu xatoni
 * yuborishdan OLDIN ushlaydi.
 *
 * <h2>⚠️ «JORIY RASM» YUKLASHDAN KEYIN YANGILANADI (BUG-034)</h2>
 * Tester «PHOTONI UPDATE QILIB BO'LMAYABDI» deb yozgan: server
 * «saqlandi» deydi, oynadagi «joriy rasm» esa ESKISI bo'lib qolaveradi.
 * Server aybsiz — o'lchov uni ko'rsatdi. Ayb shu oynada, va u IKKI
 * qismdan iborat edi:
 *
 * <ol>
 *   <li>manzil o'zgarmasdi, ya'ni `AuthedImage` qayta so'ramasdi va
 *       brauzer keshi bir soat eski baytlarni berardi — buning
 *       tuzatmasi `employeePhotoUrl` da (`version` parametri);</li>
 *   <li>⚠️ lekin bu YETARLI EMAS: `employee` — oyna ochilgan
 *       LAHZADAGI nusxa (`AttendanceEmployeesPage` uni
 *       `useState` da saqlaydi). Ro'yxat qayta o'qilsa ham shu
 *       nusxa YANGILANMAYDI, ya'ni undan olingan `photoSha256` ham
 *       eski qolardi va manzil baribir o'zgarmasdi.</li>
 * </ol>
 *
 * <p>Shuning uchun oyna o'zining {@code reloads} hisoblagichini
 * yuritadi va uni yuklash muvaffaqiyatli tugagach oshiradi. Manba
 * ATAYLAB mahalliy: u serverdan yangi qator kelishini KUTMAYDI va
 * ro'yxat keshi qachon yangilanishiga bog'liq emas.
 *
 * <p>⚠️ `present` ham hisoblagichga qaraydi. Birinchi marta rasm
 * yuklanganda `employee.hasPhoto` hamon `false` bo'ladi, `AuthedImage`
 * esa `false` da so'rovni UMUMAN yubormaydi — ya'ni yangi rasm o'rniga
 * «rasm yo'q» yozuvi qolardi. Aynan shu holat testerning gapiga eng
 * yaqin: rasm bor, ekranda esa yo'q.
 */
export function EmployeePhotoModal({ employee, onClose }: EmployeePhotoModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [result, setResult] = useState<OperationResult | null>(null)
  // ⚠️ Muvaffaqiyatli yuklashlar soni — sarlavhadagi izohga qarang.
  //    Nol bo'lsa manzil `photoSha256` ga tayanadi (kesh ishlaydi),
  //    noldan katta bo'lsa manzil MAJBURAN o'zgaradi.
  const [reloads, setReloads] = useState(0)

  // ⚠️ Oyna yopilganda holat TOZALANADI. Busiz keyingi xodim uchun
  // ochilgan oynada oldingi xodimning rasmi turardi — va u tasdiqlansa
  // butunlay boshqa odamning yuzi qurilmaga yozilardi.
  useEffect(() => {
    if (employee === null) {
      setFile(null)
      setResult(null)
      // ⚠️ Hisoblagich ham nolga qaytadi: u ANIQ shu ochilishga
      //    tegishli. Tozalanmasa keyingi xodimning oynasi darrov
      //    `?v=...-r1` bilan ochilib, keshni sababsiz chetlab o'tardi.
      setReloads(0)
    }
  }, [employee])

  // Oldindan ko'rish uchun `objectURL` — bo'shatilishi SHART
  // (`AuthedImage` dagi bilan bir xil sabab).
  useEffect(() => {
    if (file === null) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const upload = useMutation({
    mutationFn: (chosen: File) => {
      if (!employee) throw new Error('employee is null')
      return uploadEmployeePhoto(employee.id, chosen)
    },
    onSuccess: (outcome) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
      // ⚠️ `OK` bo'lsa ham oyna DARHOL yopilmaydi: yuklash rasmni
      // qurilmaga YETKAZMAYDI, faqat serverda saqlaydi va navbatga
      // qo'yadi. Natija matni aynan shuni aytadi va operator uni
      // o'qishi kerak — aks holda u rasm allaqachon qurilmada deb
      // o'ylab, xodimni eshikka yuborardi.
      setResult(localizeOutcome(outcome, 'uploadPhoto', t))
      setFile(null)
      // ⚠️ BUG-034: «joriy rasm» aynan shu qatordan yangilanadi.
      //    `invalidateQueries` yolg'iz YETMAYDI — u ro'yxat keshini
      //    yangilaydi, bu oynadagi `employee` nusxasini emas.
      setReloads((n) => n + 1)
    },
    onError: (error) => setResult({ status: 'FAILED', message: translateError(error, t) }),
  })

  const tooBig = file !== null && file.size > EMPLOYEE_PHOTO_SOFT_MAX_BYTES

  return (
    <Modal
      open={employee !== null}
      title={t('attendance.employee.photoTitle', { name: employee?.fullName ?? '' })}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>{t('common.close')}</Button>
          <Button
            type="primary"
            disabled={file === null}
            loading={upload.isPending}
            onClick={() => {
              if (file) upload.mutate(file)
            }}
          >
            {t('attendance.employee.photoUpload')}
          </Button>
        </Space>
      }
      width={520}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('attendance.employee.photoNote')}
      />

      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={result}
            closable
            onClose={() => setResult(null)}
          />
        </div>
      ) : null}

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary">
            {t('attendance.employee.photoCurrent')}
          </Typography.Text>
          <div style={{ marginTop: 6 }}>
            {employee ? (
              <AuthedImage
                url={employeePhotoUrl(
                  employee.id,
                  reloads === 0
                    ? employee.photoSha256
                    : `${employee.photoSha256 ?? 'yangi'}-r${String(reloads)}`,
                )}
                // ⚠️ `|| reloads > 0` — birinchi rasm holati. Tafsilot
                //    sarlavhadagi izohda.
                present={employee.hasPhoto || reloads > 0}
                width={120}
                height={150}
                alt={employee.fullName}
              />
            ) : null}
          </div>
        </div>

        <Upload
          accept="image/jpeg,.jpg,.jpeg"
          maxCount={1}
          // ⚠️ `beforeUpload` DOIM `false` qaytaradi: antd o'zi so'rov
          // yubormasin. Yuklashni biz qilamiz (`uploadEmployeePhoto`),
          // chunki so'rovga `Authorization` sarlavhasi va envelope
          // ochuvchi interceptor kerak — antd ikkalasini ham bilmaydi.
          beforeUpload={(chosen) => {
            if (!isJpeg(chosen)) {
              setResult({
                status: 'FAILED',
                message: t('attendance.employee.photoNotJpeg'),
              })
              return false
            }
            setResult(null)
            setFile(chosen)
            return false
          }}
          onRemove={() => {
            setFile(null)
            return true
          }}
          fileList={
            file === null
              ? []
              : ([{ uid: '1', name: file.name, status: 'done' }] as UploadFile[])
          }
        >
          <Button icon={<UploadOutlined />}>{t('attendance.employee.photoChoose')}</Button>
        </Upload>

        {file !== null && preview !== null ? (
          <Space direction="vertical" size={6}>
            <Typography.Text type="secondary">
              {t('attendance.employee.photoPreview')}
            </Typography.Text>
            <img
              src={preview}
              alt={file.name}
              style={{ width: 120, height: 150, objectFit: 'cover', borderRadius: 4 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {file.name} · {formatBytes(file.size)}
            </Typography.Text>
          </Space>
        ) : null}

        {/* ⚠️ OGOHLANTIRISH, TAQIQ EMAS — komponent sarlavhasidagi izoh.
            Matn "nima bo'lishi mumkin" ni aytadi, "mumkin emas" ni emas. */}
        {tooBig ? (
          <Alert
            type="warning"
            showIcon
            message={t('attendance.employee.photoTooBig', {
              limit: formatBytes(EMPLOYEE_PHOTO_SOFT_MAX_BYTES),
            })}
          />
        ) : null}
      </Space>
    </Modal>
  )
}
